'use client';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
	DataGridPremium,
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { useQuery } from '@parkchirp/api-client';
import { Dispatch, FC, ReactElement, ReactNode, SetStateAction, useCallback, useContext, useState } from 'react';
import { CommonContext } from '../../contexts';
import { GET_ACCOUNT_HISTORY_BALANCES_V2, GET_ACCOUNT_HISTORY_COUNT_V2, GET_ACCOUNT_HISTORY_V2 } from '../../queries';
import { datagridFilterPanelAttributes, dayjs, isAdminDashboard } from '../../utils';
import { DatagridLinearProgress } from '../loading';

type FacilityTimezone = {
	timezone: string;
};

type AccountHistoryProps = {
	tabs?: JSX.Element;
	subscriptionId: string;
	defaultOrder: Record<string, any>;
	columns: GridColDef[];
	OnSortModelChange: (props: any) => (sortModel: GridSortModel) => void;
	OnFilterModelChange: (props: any) => (filterModel: GridFilterModel) => void;
	csvExportButton: ((props: any) => ReactElement) | null;
	reportModal: ((props: any) => ReactElement) | null;
	setShowDialog: Dispatch<SetStateAction<ReactNode>>;
	facilityTimezone?: FacilityTimezone;
	tenantSubscriptionId?: string;
};

const formatCurrency = (value?: number) =>
	(value ? value / 100 : 0).toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});

const Row = ({ label, value }: { label: string; value: number }) => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			border: '1px solid gray',
			p: 1,
			mr: 1,
		}}
	>
		<Typography variant='h6' sx={{ mr: 1 }}>
			{label}
		</Typography>
		<Typography variant='h6'>{formatCurrency(value)}</Typography>
	</Box>
);

const CustomToolbar = ({ subscriptionId, filters, sortOrder, tenantSubscriptionId, csvExportButton }) => (
	<GridToolbarContainer>
		<GridToolbarFilterButton onResize={undefined} onResizeCapture={undefined} data-cy='filter-table-button' />
		<GridToolbarColumnsButton onResize={undefined} onResizeCapture={undefined} />
		<GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
		{csvExportButton
			? csvExportButton({
					endpoint: '/account_history_report',
					fileName: `account_history_data_${subscriptionId}`,
					args: {
						filters,
						subscriptionId,
						sortOrder,
						tenantSubscriptionId,
					},
				})
			: null}
	</GridToolbarContainer>
);

export const AccountHistory: FC<AccountHistoryProps> = ({
	tabs = null,
	setShowDialog,
	columns,
	defaultOrder,
	OnSortModelChange,
	OnFilterModelChange,
	reportModal,
	csvExportButton,
	subscriptionId,
	facilityTimezone,
	tenantSubscriptionId,
}) => {
	const { hasComponentAccess } = useContext(CommonContext);
	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [sortOrder, setSortOrder] = useState(defaultOrder);
	const [filters, setFilters] = useState([]);
	const {
		loading,
		data: accountHistoryData,
		// refetch,
	} = useQuery(GET_ACCOUNT_HISTORY_V2, {
		skip: !subscriptionId,
		variables: {
			subscriptionId,
			filters,
			limit: pageSize,
			offset: page * pageSize,
			order: sortOrder,
			tenantSubscriptionId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'cache-and-network',
	});

	const { data: countData } = useQuery(GET_ACCOUNT_HISTORY_COUNT_V2, {
		skip: !subscriptionId,
		variables: {
			subscriptionId,
			filters,
			tenantSubscriptionId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'cache-and-network',
	});
	const accountHistoryCount = countData?.getAccountHistoryCount?.total || 0;

	const { data: historyBalances, loading: balancesLoading } = useQuery(GET_ACCOUNT_HISTORY_BALANCES_V2, {
		skip: !subscriptionId,
		variables: {
			subscriptionId,
			tenantSubscriptionId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
	});

	const openModal = () => {
		setShowDialog(
			reportModal
				? reportModal({
						title: 'Export Data',
						reportTypes: [
							{
								label: 'Outstanding Balances Report',
								value: 'OutstandingBalanceReport',
							},
							{
								label: 'Outstanding Balances Summary Report',
								value: 'OutstandingBalanceSummaryReport',
							},
						],
						subId: accountHistoryData?.getAccountHistory.data[0]?.subId,
						dismissText: 'Cancel',
						confirmText: 'Download',
						handleDismiss: () => setShowDialog(null),
						fileOptions: ['CSV', 'PDF'],
					})
				: null,
		);
	};

	const accountHistoryRows = accountHistoryData?.getAccountHistory.data.map((ac: any, index: number) => {
		const {
			accountHistoryType,
			invoiceReadableId,
			creditReadableId,
			creditId,
			invoiceId,
			amount,
			createdAt,
			timezone,
			paymentMetaId,
			paymentGroupId,
			dueOn,
			paymentType,
			creditAmount,
			subId,
			applicationDate,
		} = ac;
		const negativeTypes = [
			'Credit Write Off - Cash Applied',
			'Payment - Credit No Cash Applied',
			'Payment - Credit Cash Applied',
			'Payment - Cash Applied',
			'Payment - Discount Credit Applied',
			'Payment - Carryover Credit Applied',
			'Payment',
			'Credit Write Off - Cash Not Applied',
			'Credit Write Off - No Cash Applied',
			'Credit - Discount Credit Write Off',
			'Credit - Carryover Credit Write Off',
			'Reversal - Credit Cash Applied',
			'Reversal - Credit No Cash Applied',
		];
		const showCreditAmount = ['Reversal - Payment', 'Reversal - Credit Payment'];
		const isNegative = negativeTypes.includes(accountHistoryType);
		if (facilityTimezone) {
			facilityTimezone.timezone = timezone;
		}
		return {
			id: index,
			accountHistoryType,
			invoiceReadableId: invoiceReadableId || '-',
			creditReadableId: creditReadableId || '-',
			creditId,
			invoiceId,
			amount: amount ? amount / 100 : '-',
			timezone: dayjs().tz(timezone).format('zzz'),
			createdAt: dayjs(createdAt).tz(timezone).format('MM/DD/YYYY hh:mm a'),
			paymentMetaId: paymentMetaId || '-',
			paymentGroupId,
			dueOn: dueOn ? dayjs(dueOn).format('MM/DD/YYYY') : '-',
			paymentType,
			creditAmount: showCreditAmount.includes(accountHistoryType) ? '-' : creditAmount ? creditAmount / 100 : '-',
			isNegative,
			subId,
			applicationDate,
		};
	});

	const onSortModelChange = useCallback(
		(sortModel: GridSortModel) => {
			OnSortModelChange({ setPaginationModel, setSortOrder })(sortModel);
		},
		[setPaginationModel, setSortOrder, OnSortModelChange],
	);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			OnFilterModelChange({ setFilters, setPaginationModel })(filterModel);
		},
		[setFilters, setPaginationModel, OnFilterModelChange],
	);

	if (balancesLoading) {
		return (
			<div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
				<CircularProgress className='account-history-loading' />
			</div>
		);
	}

	const rows = [
		{ label: 'Current Balances:', key: 'current' },
		{ label: '1 to 30 days late:', key: 'days1to30' },
		{ label: '31 to 60 days late:', key: 'days31to60' },
		{ label: '61 to 90 days late:', key: 'days61to90' },
		{ label: '91+ days late:', key: 'days91Plus' },
		{ label: 'Total Late:', key: 'totalLate' },
	];

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			{tabs}
			<Grid style={{ marginBottom: '20px' }} spacing={2} container>
				<Grid item xs={3}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							border: '1px solid gray',
							padding: '8px',
						}}
					>
						<Typography variant='h6' style={{ fontWeight: 'bold' }} data-cy='account-balance-text'>
							Account Balance:
						</Typography>
						<Typography variant='h6' style={{ fontWeight: 'bold' }}>
							{historyBalances?.getAccountHistoryBalances?.totalInvoiceBalance
								? (historyBalances.getAccountHistoryBalances.totalInvoiceBalance / 100).toLocaleString(
										'en-US',
										{
											style: 'currency',
											currency: 'USD',
										},
									)
								: '$0.00'}
						</Typography>
					</div>
				</Grid>
				<Grid item xs={3}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							border: '1px solid gray',
							padding: '8px',
						}}
					>
						<Typography variant='h6' style={{ fontWeight: 'bold' }} data-cy='credit-balance-text'>
							Credit Balance:
						</Typography>
						<Typography variant='h6' style={{ fontWeight: 'bold' }}>
							{historyBalances?.getAccountHistoryBalances?.totalCreditBalance
								? (historyBalances.getAccountHistoryBalances.totalCreditBalance / 100).toLocaleString(
										'en-US',
										{
											style: 'currency',
											currency: 'USD',
										},
									)
								: '$0.00'}
						</Typography>
					</div>
				</Grid>
				<Grid item xs={3} />
				<Grid item xs={3}>
					{reportModal &&
					accountHistoryCount &&
					(!isAdminDashboard || hasComponentAccess('SubscriptionInfoHistoryPageDownloadOBReportButton')) ? (
						<Button
							onClick={openModal}
							className='pca__manualInvoiceForm-addRowButton'
							data-cy='download-outstanding-balance-reports-button'
						>
							Download Outstanding Balance Reports
						</Button>
					) : null}
				</Grid>
				<Grid display='flex' item xs={12}>
					{rows.map(({ label, key }) => (
						<Row key={key} label={label} value={accountHistoryData?.getAccountHistory.aging[key] ?? 0} />
					))}
				</Grid>
			</Grid>

			<div className='pca__datagrid-sticky--headfoot' data-cy='account-history-table-container'>
				<DataGridPremium
					sx={{ height: '100%' }}
					paginationModel={{ page, pageSize }}
					onSortModelChange={onSortModelChange}
					filterMode='server'
					onFilterModelChange={onFilterModelChange}
					hideFooter={false}
					hideFooterPagination={false}
					onPaginationModelChange={setPaginationModel}
					pagination
					rows={accountHistoryRows || []}
					rowCount={accountHistoryCount}
					pageSizeOptions={[pageSize]}
					columns={columns}
					paginationMode='server'
					sortingMode='server'
					loading={loading}
					slots={{
						toolbar: CustomToolbar,
						loadingOverlay: DatagridLinearProgress,
					}}
					slotProps={{
						toolbar: {
							subscriptionId,
							filters,
							sortOrder,
							tenantSubscriptionId,
							csvExportButton,
						},
						filterPanel: { ...datagridFilterPanelAttributes },
					}}
				/>
			</div>
		</div>
	);
};
